"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useSidebar } from "../context/SidebarContext";
import {
  BuildingMaterialsIcon,
  BoxCubeIcon,
  BoxIcon,
  CalenderIcon,
  ChevronDownIcon,
  DataReportIcon,
  DocsIcon,
  DollarLineIcon,
  FileIcon,
  GridIcon,
  HorizontaLDots,
  InsertCreditCardIcon,
  ListIcon,
  MoneyBillWaveIcon,
  MoneyBillsSimpleIcon,
  PageIcon,
  PayrollIcon,
  PieChartIcon,
  PlugInIcon,
  SellerIcon,
  ShippingFastIcon,
  TaxIcon,
  TableIcon,
  UserCircleIcon,
  WarehouseAltIcon,
} from "../icons/index";

/** Sidebar structure matches RMC frontend guide (routing & navigation). */
const navSections = [
  {
    key: "dash",
    heading: "Menu",
    items: [
      {
        icon: <GridIcon />,
        name: "Dashboard",
        path: "/",
      },
    ],
  },
  {
    key: "masters",
    heading: "Masters",
    items: [
      { icon: <BoxCubeIcon />, name: "Company & FY", path: "/company" },
      { icon: <BuildingMaterialsIcon />, name: "Raw Materials", path: "/purchase/materials" },
      {
        icon: <DocsIcon />,
        name: "Grades & Recipes",
        subItems: [
          { name: "Grades", path: "/items/grades" },
          { name: "Recipes", path: "/items/recipes" },
        ],
      },
      { icon: <UserCircleIcon />, name: "Drivers", path: "/transport/drivers" },
      { icon: <ShippingFastIcon />, name: "Vehicles", path: "/transport/vehicles" },
      { icon: <PlugInIcon />, name: "Fuel readings", path: "/transport/fuel-readings" },
      {
        icon: <InsertCreditCardIcon />,
        name: "Vehicle EMI finance",
        path: "/transport/vehicle-finance",
      },
    ],
  },
  {
    key: "gate",
    heading: "Gatekeeping",
    items: [
      {
        icon: <ListIcon />,
        name: "Gatekeeping",
        subItems: [
          { name: "Inward", path: "/gatekeeping/inward" },
          { name: "Outward", path: "/gatekeeping/outward" },
        ],
      },
    ],
  },
  {
    key: "purchase",
    heading: "Purchase",
    items: [
      { icon: <PageIcon />, name: "Parties", path: "/purchase/parties" },
      { icon: <PayrollIcon />, name: "Concrete quotations", path: "/purchase/concrete-quotations" },
      {
        icon: <MoneyBillsSimpleIcon />,
        name: "Challans",
        subItems: [
          { name: "List", path: "/purchase/challans" },
          { name: "New", path: "/purchase/challans/new" },
        ],
      },
      { icon: <DocsIcon />, name: "Invoices", path: "/purchase/invoices" },
    ],
  },
  {
    key: "sales",
    heading: "Sales",
    items: [
      { icon: <SellerIcon />, name: "Parties & Sites", path: "/sales/parties" },
      { icon: <PayrollIcon />, name: "Quotations", path: "/sales/quotations" },
      { icon: <DocsIcon />, name: "Invoices", path: "/sales/invoices" },
      { icon: <DataReportIcon />, name: "Reports", path: "/accounts/reports" },
      {
        icon: <MoneyBillsSimpleIcon />,
        name: "Challans",
        subItems: [
          { name: "List", path: "/sales/challans" },
          { name: "New", path: "/sales/challans/new" },
          { name: "Bulk Create", path: "/sales/challans/bulk" },
        ],
      },
    ],
  },
  {
    key: "stock",
    heading: "Stock",
    items: [
      {
        icon: <WarehouseAltIcon />,
        name: "Stock",
        subItems: [
          { name: "Current Stock", path: "/stock" },
          { name: "Reports", path: "/stock/reports" },
        ],
      },
    ],
  },
  {
    key: "accounts",
    heading: "Accounts",
    items: [
      { icon: <TaxIcon />, name: "GST Master", path: "/accounts/gst-master" },
      { icon: <TableIcon />, name: "Misc Expenses", path: "/accounts/misc-expenses" },
      { icon: <MoneyBillWaveIcon />, name: "Payments", path: "/accounts/payments" },
      { icon: <InsertCreditCardIcon />, name: "Receipts", path: "/accounts/receipts" },
      { icon: <PieChartIcon />, name: "Party Ledger", path: "/accounts/ledger" },
      { icon: <DataReportIcon />, name: "Trial Balance", path: "/accounts/trial-balance" },
    ],
  },
];

const AppSidebar = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const pathname = usePathname();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [subMenuHeight, setSubMenuHeight] = useState({});
  const subMenuRefs = useRef({});

  const isActive = useCallback((path) => path === pathname, [pathname]);

  const renderMenuItems = (items, menuType) => (
    <ul className="flex flex-col gap-1">
      {items.map((nav, index) => (
        <li key={`${menuType}-${nav.name}-${index}`}>
          {nav.subItems ? (
            <button
              type="button"
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`menu-item group  ${
                openSubmenu?.type === menuType && openSubmenu?.index === index
                  ? "menu-item-active"
                  : "menu-item-inactive"
              } cursor-pointer ${
                !isExpanded && !isHovered
                  ? "lg:justify-center"
                  : "lg:justify-start"
              }`}
            >
              <span
                className={`${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-icon-active"
                    : "menu-item-icon-inactive"
                }`}
              >
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <span className={`menu-item-text`}>{nav.name}</span>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDownIcon
                  className={`ml-auto w-4 h-4 transition-transform duration-200  ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? "rotate-180 text-white"
                      : ""
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                href={nav.path}
                className={`menu-item group ${
                  isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                }`}
              >
                <span
                  className={`${
                    isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className={`menu-item-text`}>{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${menuType}-${index}`] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
              }}
            >
              <ul className="mt-1 space-y-0.5 ml-7">
                {nav.subItems.map((subItem) => (
                  <li key={subItem.path}>
                    <Link
                      href={subItem.path}
                      className={`menu-dropdown-item ${
                        isActive(subItem.path)
                          ? "menu-dropdown-item-active"
                          : "menu-dropdown-item-inactive"
                      }`}
                    >
                      {subItem.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  useEffect(() => {
    let submenuMatched = false;
    navSections.forEach((section) => {
      section.items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({ type: section.key, index });
              submenuMatched = true;
            }
          });
        }
      });
    });
    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [pathname, isActive]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index, menuType) => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  return (
    <aside
      className={`fixed mt-14 flex flex-col lg:mt-0 top-0 px-3 left-0 bg-gray-950 border-gray-800/50 text-gray-300 h-screen transition-all duration-300 ease-in-out z-50 border-r 
        ${
          isExpanded || isMobileOpen
            ? "w-[240px]"
            : isHovered
            ? "w-[240px]"
            : "w-[72px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-5 flex  ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link href="/">
          {isExpanded || isHovered || isMobileOpen ? (
            <>
              <Image
                src="/images/logo/logo_white.svg"
                alt="RMCOP"
                width={168}
                height={48}
                className="object-contain"
              />
            </>
          ) : (
            <Image
              src="/images/logo/logo_white.svg"
              alt="RMCOP"
              width={34}
              height={34}
              className="object-contain"
            />
          )}
        </Link>
      </div>
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-4">
          <div className="flex flex-col gap-4">
            {navSections.map((section) => (
              <div key={section.key}>
                <h2
                  className={`mb-2 text-[10px] uppercase flex leading-[16px] text-gray-600 tracking-wider ${
                    !isExpanded && !isHovered
                      ? "lg:justify-center"
                      : "justify-start"
                  }`}
                >
                  {isExpanded || isHovered || isMobileOpen ? (
                    section.heading
                  ) : (
                    <HorizontaLDots />
                  )}
                </h2>
                {renderMenuItems(section.items, section.key)}
              </div>
            ))}
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;
